/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "axios";
import BASEUtils from "../../../../@BASE/utils";
import { addCourse, removeCourse, updateCourse } from "./courseSlice";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getCourses = createAsyncThunk(
  "courses/getCourses",
  async (params, { dispatch, getState }) => {
    try {
      // send request to get courses
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses`
      );
      // get data from response
      const data = await response.data;
      // return data
      return { data };
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: error.message, variant: "error" }));
    }
  }
);

// create entity adapter
const coursesAdapter = createEntityAdapter({});

// export selectors
export const selectSearchText = ({ courses }) => courses.list.searchText;
export const { selectAll: selectCourses, selectById: selectCoursesById } = coursesAdapter.getSelectors((state) => state.courses.list);
export const selectFilteredCourses = createSelector(
  [selectCourses, selectSearchText],
  (courses, searchText) => {
    if (searchText.length === 0) {
      return courses;
    }
    return BASEUtils.filterArrayByString(courses, searchText);
  }
);
export const selectGroupedFilteredCourses = createSelector(
  [selectFilteredCourses],
  (courses) => {
    return courses
      .sort((a, b) =>
        a.title.localeCompare(b.title, "es", { sensitivity: "base" })
      )
      .reduce((r, e) => {
        // get first letter of title of current element
        const group = e.project_id;
        // if there is no property in accumulator with this letter create it
        if (!r[group]) r[group] = { group, children: [e] };
        // if there is push current element to children array for that letter
        else r[group].children.push(e);
        // return accumulator
        return r;
      }, {});
  }
);

// create slice
const coursesSlice = createSlice({
  name: "courses",
  initialState: coursesAdapter.getInitialState({
    searchText: "",
  }),
  reducers: {
    setCoursesSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || "" }),
    },
  },
  extraReducers: {
    [updateCourse.fulfilled]: coursesAdapter.upsertOne,
    [addCourse.fulfilled]: coursesAdapter.addOne,
    [removeCourse.fulfilled]: (state, action) =>
      coursesAdapter.removeOne(state, action.payload),
    [getCourses.fulfilled]: (state, action) => {
      const { data, routeParams } = action.payload;
      coursesAdapter.setAll(state, data);
      state.searchText = "";
    },
  },
});

// export actions
export const { setCoursesSearchText } = coursesSlice.actions;

export const selectCourseEntities = ({ courses }) => courses.list.entities;

// export reducer
export default coursesSlice.reducer;
